import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";

import { setWidgetHoverAtom } from "atoms/interaction";
import { EvaChart } from "constants/charts.enums";
import { EntityType } from "constants/entities.enum";
import { ChartStatus, useChartsContext } from "contexts/ChartContext";
import { UNDEFINED_LOCKS, UNDEFINED_MPC_LOCKS } from "entities/charts/chart";
import { createFeatureEntities } from "entities/charts/eva/features";
import { createEntityState } from "entities/charts/factory";
import { useSetAtom } from "jotai";
import { createChart, removeChart } from "store/features/charts/chartSlice";
import styled from "styled-components/macro";
import { isAllStatusesRequest } from "types";
import { noop } from "utils";

import {
  WELL_STATUS_COUNTS_MODEL,
  useChartEntities,
  useOpenLegendWhen,
  useWellStatusSession
} from "hooks/charts";
import type { ChartSettings } from "hooks/charts";
import useChartSize from "hooks/charts/echart/useChartSize";

import { ResultMessageType } from "models/model";

import { ErrorBoundary } from "components/base";
import { AxisContainer } from "components/charts/components/AxisContainer/AxisContainer";
import Screenshot from "components/charts/components/Screenshot";
import { ChartLegend } from "components/charts/legend";
import Indicator from "components/ui/Indicator";

import WellStatusChart from "./WellStatusChart";
import WellStatusChartFocusBorder from "./WellStatusChartFocusBorder";
import WellStatusChartToolbar from "./WellStatusChartToolbar";
import WellStatusDataTable from "./WellStatusDataTable";
import WellStatusSelector from "./WellStatusSelector";
import WellStatusTimeScaleSelector from "./WellStatusTimeScaleSelector";

interface WellStatusChartWrapperT {
  className?: string;
  onFullscreen?: (b: boolean) => void;
}

/**
 * Slim sibling of ChartWrapper for the Well Status widget. Owns the chart
 * entity lifecycle (createChart/removeChart), the scoped toolbar, the raw data
 * table, the y-axis lock handles, the legend and screenshot plumbing.
 */
function WellStatusChartWrapper({
  className = "",
  onFullscreen = noop
}: WellStatusChartWrapperT) {
  const { id, status, initialChartSettings } = useChartsContext();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const setWidgetHover = useSetAtom(setWidgetHoverAtom);

  const screenshotContainerId = `screenshot-container-${id}`;

  const {
    fullScreen,
    screenshot,
    chartLegend,
    bounds,
    dataTable,
    apiResponse,
    chartType,
    chartFocus,
    request
  } = useChartEntities(id);

  const isChartFocusActive = chartFocus?.active === true;
  // The explicit flag, never a magic `status` value — the selector translates its
  // "All Statuses" dropdown entry into this.
  const isAllStatusesMode = isAllStatusesRequest(request);

  const isFullscreenActive = fullScreen?.active;
  const isScreenshotActive = screenshot?.active;
  const isDataTableActive = dataTable?.active;
  // Only the over-time chart has a time axis and a status-valued y-axis.
  const isOverTime = chartType === EvaChart.WellStatusOverTime;

  // Backend/gRPC failures come back as HTTP 200 with a GeneralError message —
  // surface them as a failure, NOT "No Data Available", so the user can tell an
  // outage from a genuinely empty result.
  const isServerError = apiResponse?.messageType === ResultMessageType.GeneralError;
  // chart-svc distinguishes its failures ("The active filter could not be
  // found. Re-apply the filter and try again." vs. a generic load failure), so
  // show what it actually said. A blanket "Failed to Load Chart Data" threw away
  // the one sentence that told the user what to DO about it.
  const serverErrorMessage = apiResponse?.messages?.find((m) => m.message)?.message;
  const isMissingData =
    !apiResponse ||
    (apiResponse.series && apiResponse.series.length === 0) ||
    apiResponse.messageType === ResultMessageType.NoData;

  const showChartLegend =
    chartLegend?.active && !isScreenshotActive && !isDataTableActive && bounds?.width > 0;

  useChartSize(id, wrapperRef);
  // Write half of the dashboard round-trip: mirrors RigFracChartWrapper. Without
  // it `getSessionData` finds nothing under this widget's key and a dashboard save
  // persists the widget's DEFAULT componentState over the user's selections.
  useWellStatusSession(id);
  // Open the legend when the chart stops matching the map's grouping, so its
  // colours are named on screen. Chart Focus regroups this chart alone; All
  // Statuses stacks by STATUS across the bins, so the map's bin legend describes
  // the wrong dimension entirely. One combined condition, not two calls — see the
  // hook's note on the first-render guard.
  useOpenLegendWhen(id, isChartFocusActive || isAllStatusesMode);
  useEffect(() => onFullscreen(isFullscreenActive), [isFullscreenActive]);

  // Initial chart creation on first render (mirrors ChartWrapper). SEED from
  // initialChartSettings (the widget's componentState, restored from a saved
  // dashboard) so the user's chart type / features / request selections come back;
  // fall back to Status Over Time on a fresh widget.
  useEffect(() => {
    const seed = initialChartSettings as unknown as Partial<ChartSettings> & {
      request?: GenericObject;
      countsModel?: string;
    };
    const seedChartType =
      seed?.chartType === EvaChart.WellStatusCurrent
        ? EvaChart.WellStatusCurrent
        : EvaChart.WellStatusOverTime;

    // Drop y-axis locks saved before the over-time axis changed meaning.
    //
    // It used to count wells HOLDING the status at each period end and now counts
    // wells ENTERING it during the period (EVA-42) — one to two orders apart. A
    // y-max locked at the old scale flattens every bar to invisibility, with no
    // error and nothing on screen to explain it, so the widget reads as broken.
    // Settings written since carry `countsModel`; those without it predate the
    // change and their locks are meaningless rather than merely stale.
    //
    // Scoped to the over-time chart: the current-status chart counts wells either
    // way, so its locks are unaffected.
    const locksArePreEntryModel =
      seedChartType === EvaChart.WellStatusOverTime &&
      seed?.countsModel !== WELL_STATUS_COUNTS_MODEL;

    dispatch(
      createChart({
        id,
        ...createEntityState(EntityType.Chart, {
          id,
          chartType: seedChartType,
          features: createFeatureEntities(seedChartType, seed?.features),
          locks: locksArePreEntryModel ? UNDEFINED_LOCKS : seed?.locks ?? UNDEFINED_LOCKS,
          mpcLocks: seed?.mpcLocks ?? UNDEFINED_MPC_LOCKS,
          // Seed the persisted selections; the controller rebuilds the full
          // request (adding the live filter + focus) from them.
          request: seed?.request as never,
          // Valid JSON from the start: Screenshot JSON.parses entity options.
          options: "{}"
        })
      })
    );

    return () => {
      dispatch(removeChart(id));
    };
    // Seed once on mount; initialChartSettings is stable per widget mount and must
    // NOT be a dep (a new identity would remove+recreate the entity, wiping live
    // state). createChart is a no-op if the entity already exists.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, dispatch]);

  // Hover registration drives toolbar/selector visibility (the same
  // widgetHoverAtom key every chart hook reads as isHover).
  const handleMouseEnter = () => {
    setWidgetHover({ widgetId: id, isHover: true });
  };
  const handleMouseLeave = () => {
    setWidgetHover({ widgetId: id, isHover: false });
  };

  return (
    <ErrorBoundary>
      <Wrapper
        ref={wrapperRef} // for useChartSize
        className={className}
        data-testid="wellStatusChartWrapper"
        $screenshot={isScreenshotActive}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}>
        <ChartContainer $screenshot={isScreenshotActive}>
          <WellStatusChart />
        </ChartContainer>

        {/* Data table overlays the chart when toggled on (export lives inside it). */}
        {isDataTableActive && <WellStatusDataTable />}

        {status === ChartStatus.Idle &&
          (isMissingData || isServerError) &&
          !isDataTableActive && (
            <Indicator
              message={
                isServerError
                  ? serverErrorMessage || "Failed to Load Chart Data"
                  : "No Data Available"
              }
            />
          )}

        <WellStatusChartToolbar />

        {showChartLegend && (
          <ChartLegend
            parentDimensions={{ width: bounds.width, height: bounds.height }}
            id={screenshotContainerId + "chart-legend"}
            screenshot={screenshot}
            bounds={bounds}
            legend={chartLegend}
          />
        )}

        {/* Axis-mounted controls: the status picker overlays the
            axis title it drives — the y-axis on the over-time chart, and the
            bottom value axis on the horizontal current chart, whose bars ARE the
            "<status> Count". The time scale sits on the over-time x-axis. Same
            convention as the rig/frac charts. */}
        {!isDataTableActive && (
          <>
            <WellStatusSelector placement={isOverTime ? "yAxis" : "xAxis"} />
            {isOverTime && <WellStatusTimeScaleSelector />}
          </>
        )}

        {/* Y-axis lock handles — Status Over Time only (the current chart's value
            axis is horizontal and ships no locks). Self-gates on
            hover/screenshot/data-table and reads its lock config from the
            persisted options JSON dispatched by useWellStatusChartOptions. */}
        {isOverTime && !isDataTableActive && <AxisContainer />}

        <Screenshot containerId={screenshotContainerId} />

        {/* Last child so the dashed frame draws over the toolbar and axis
            controls, matching the wells widget. It is pointer-events:none, so it
            never intercepts a click. */}
        <WellStatusChartFocusBorder />
      </Wrapper>
    </ErrorBoundary>
  );
}

export default WellStatusChartWrapper;

const Wrapper = styled.div<{ $screenshot?: boolean }>`
  width: 100%;
  height: 100%;
  min-width: 0;
  display: grid;
  grid-template-rows: minmax(0, 1fr);
  border-radius: var(--border-radius);
  box-shadow: rgba(var(--color-shadow-rgb), 0.2) 0 0 1px;
  padding-top: 10px;
  z-index: 0;
  position: relative;
  /* Grey backdrop behind the centered chart card while screenshotting — matches
     the shared wells ChartWrapper. */
  background-color: ${(props) =>
    props.$screenshot ? "rgba(46, 72, 88, 0.24)" : "transparent"};

  &.fullscreen {
    width: 100vw;
    height: 100vh;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: var(--index-fullscreen);
  }
`;

const ChartContainer = styled.div<{ $screenshot?: boolean }>`
  min-height: 0;
  ${(props) =>
    props.$screenshot
      ? `
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: auto;
  `
      : ""}

  &:hover {
    z-index: 1;
  }
`;
